<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed, onMounted, ref, watch } from "vue";
import { SatItemDisplay } from "@chatfood/design-system";
import type { ICrossSellItemsEmits } from "./emits";
import type { ICrossSellItemsProps } from "./props";
import { useBugReporter, useDirectBackend, type IDomainMoney } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { ItemModal } from "~/modals/item";
import { useNavigation } from "~/composables/use-navigation";

const css = bemBuilder("cross-sell-items");
const emits = defineEmits<ICrossSellItemsEmits>();
const props = defineProps<ICrossSellItemsProps>();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const { basketRepo } = useDirectBackend();
const { report } = useBugReporter();
const menuId = ref("");
const { rawPush } = useNavigation();

const crossSellItems = ref<
  Awaited<ReturnType<typeof basketRepo.getCrossSellItems>>["data"]
>([]);

const discountedProducts = computed(() => {
  const discount = basket.discount;

  if (!discount) return {};

  if (discount.unit !== "percentage") return {};

  const items = crossSellItems.value.flat();

  const products = discount.discountedProducts;
  const discountAmount = discount.amount;

  return items.reduce(
    (acc, item) => {
      const isItemDiscounted =
        products.length === 0 || products.includes(item.plu);
      const price = !isItemDiscounted
        ? item.price
        : {
            value: item.price.value * (1 - discountAmount / 100),
            currency: item.price.currency,
          };

      return {
        ...acc,
        [item.plu]: price,
      };
    },
    {} as Record<string, IDomainMoney>,
  );
});

const fetchCrossSellItems = async () => {
  try {
    const res = await basketRepo.getCrossSellItems({
      businessId: business.id,
      outletId: outlet.id,
      orderingMode: basket.orderingMode,
      salesChannel: basket.salesChannel,
    });

    // Filter out duplicates based on item id
    const uniqueItems = res.data.filter(
      (item, index, self) => index === self.findIndex((i) => i.id === item.id),
    );

    crossSellItems.value = uniqueItems;
    emits("hasItems", uniqueItems.length > 0);
  } catch (e) {
    emits("hasItems", false);
    report(e);
  }
};

const handleItemClick = (id: string, menu: string) => {
  menuId.value = menu;
  rawPush({
    query: {
      "item-id": id,
    },
  });
};

const handleItemModalHidden = () => {
  menuId.value = "";
};

watch(
  () => basket.items,
  (newItems, oldItems) => {
    if (newItems.length !== oldItems.length) {
      fetchCrossSellItems();
    }
  },
  { deep: true },
);

onMounted(() => {
  fetchCrossSellItems();
});
</script>

<template>
  <div
    :class="{ [css('')]: true, [css('', 'column')]: props.view === 'column' }"
  >
    <SatItemDisplay
      v-for="item in crossSellItems"
      :id="item.id"
      :key="item.id"
      :class="css('items')"
      view="col"
      :name="item.name"
      :original-amount="item.price"
      :description="item.description"
      :amount="discountedProducts[item.plu] || item.price"
      :photo="item.photo"
      :has-allergen-items="Boolean(item.allergens)"
      :consumables="item.consumables"
      @click="handleItemClick(item.id, item.menuId)"
    />
    <ItemModal :menu-id="menuId" @hidden="handleItemModalHidden" />
  </div>
</template>

<style lang="scss" scoped>
.cross-sell-items {
  display: flex;
  overflow-y: scroll;
  padding-left: var(--spacing-md);
  gap: var(--spacing-md);
  padding-bottom: var(--spacing-md);
  width: 100%;

  &--column {
    flex-direction: column;
    padding-left: 0;
  }

  &__items {
    min-width: 90%;
    border: 1px solid var(--color-base-80);
    border-radius: var(--radii-card-md);
    padding: var(--spacing-md);
    cursor: pointer;
  }
}
</style>
